// zoom-dialog-swipe.js — vertical mode aware, no pointer capture
(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[zoom-swipe]', ...a);

  const THRESH_X = 60;
  const MAX_OFF_AXIS = 40;
  const ZOOM_TOL = 1.05;
  const COOLDOWN_MS = 300;

  const isMobileLike = () => matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024;
  const findDialog = (root=document) => root.querySelector('zoom-dialog[data-sync-wired="true"]');
  const findGallery = (zd) => zd && zd.querySelector('.dialog-zoomed-gallery');
  const findThumbActive = (zd) => zd && zd.querySelector('.dialog-thumbnails-list__thumbnail[aria-selected="true"]');

  const goBy = (zd, dir) => {
    const cur = findThumbActive(zd);
    if (!cur) return;
    const btn = dir > 0 ? cur.nextElementSibling : cur.previousElementSibling;
    if (btn && btn.classList.contains('dialog-thumbnails-list__thumbnail')) btn.click();
  };

  const isZoomedIn = (zd) => {
    const img = zd?.querySelector('.dialog-zoomed-gallery .product-media__image');
    if (!img) return false;
    const t = getComputedStyle(img).transform;
    if (t && t !== 'none') {
      const m = t.match(/matrix\(([^)]+)\)/);
      if (m?.[1]) {
        const a = parseFloat(m[1].split(',')[0]);
        if (!Number.isNaN(a) && a > ZOOM_TOL) return true;
      }
    }
    const wrap = img.closest('.product-media__drag-zoom-wrapper') || img.parentElement;
    if (wrap) {
      if ((img.scrollWidth - wrap.clientWidth > 8) || (img.scrollHeight - wrap.clientHeight > 8)) return true;
    }
    return false;
  };

  // Skip binding if zoom-dialog has vertical paging enabled
  const isVerticalPaging = (zd) => zd?.dataset.verticalPager === '1';

  const ensureTouchAction = (zd) => {
    const g = findGallery(zd);
    if (g) {
      g.style.touchAction = 'pan-y pinch-zoom';
      g.style.webkitOverflowScrolling = 'touch';
    }
    zd?.querySelectorAll('.product-media__drag-zoom-wrapper, .dialog-zoomed-gallery .product-media__image')
      .forEach(el => {
        el.style.touchAction = 'pan-y pinch-zoom';
        el.style.webkitOverflowScrolling = 'touch';
      });
  };

  const bindSwipe = (root=document) => {
    const zd = findDialog(root);
    if (!zd) return;
    if (zd.dataset.swipeBound === '1') return;

    const gallery = findGallery(zd);
    if (!gallery) return;

    if (isVerticalPaging(zd)) {
      log('↘️ skip binding (vertical pager active)');
      zd.dataset.swipeBound = '1';
      return;
    }

    ensureTouchAction(zd);

    let lastSwipe = 0;
    const USE_POINTER = 'onpointerdown' in window;

    if (USE_POINTER) {
      let tracking = false, sx = 0, sy = 0, id = null;

      const canHandlePointer = (e) => {
        if (e.pointerType === 'touch') return true;
        if (e.pointerType === 'mouse' && isMobileLike()) return true;
        return false;
      };

      const onPointerDown = (e) => {
        if (!canHandlePointer(e) || isZoomedIn(zd) || isVerticalPaging(zd)) return;
        tracking = true; id = e.pointerId; sx = e.clientX; sy = e.clientY;
        // NO pointer capture — allow native vertical scrolling
      };

      const onPointerMove = (e) => {
        if (!tracking || e.pointerId !== id) return;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        // Vertical intent? let the browser scroll and stop tracking.
        if (Math.abs(dy) > Math.abs(dx)) { tracking = false; id = null; return; }
        if (Math.abs(dx) > THRESH_X && Math.abs(dy) < MAX_OFF_AXIS && !isZoomedIn(zd)) {
          const now = Date.now();
          if (now - lastSwipe > COOLDOWN_MS) {
            goBy(zd, dx < 0 ? +1 : -1);
            lastSwipe = now;
          }
          tracking = false; id = null;
        }
      };

      const onPointerEnd = () => { tracking = false; id = null; };

      gallery.addEventListener('pointerdown', onPointerDown, { passive: true });
      gallery.addEventListener('pointermove', onPointerMove, { passive: true });
      gallery.addEventListener('pointerup', onPointerEnd, { passive: true });
      gallery.addEventListener('pointercancel', onPointerEnd, { passive: true });
      log('✅ bound (pointer, no capture)');
    } else {
      // Touch fallback (older iOS)
      let tActive = false, tsX = 0, tsY = 0;

      const onTouchStart = (e) => {
        if (isZoomedIn(zd) || isVerticalPaging(zd)) return;
        const t = e.touches?.[0]; if (!t) return;
        tActive = true; tsX = t.clientX; tsY = t.clientY;
      };

      const onTouchMove = (e) => {
        if (!tActive || isZoomedIn(zd)) return;
        const t = e.touches?.[0]; if (!t) return;
        const dx = t.clientX - tsX, dy = t.clientY - tsY;
        if (Math.abs(dy) > Math.abs(dx)) { tActive = false; return; }
        if (Math.abs(dx) > THRESH_X && Math.abs(dy) < MAX_OFF_AXIS) {
          const now = Date.now();
          if (now - lastSwipe > COOLDOWN_MS) {
            goBy(zd, dx < 0 ? +1 : -1);
            lastSwipe = now;
          }
          tActive = false;
        }
      };

      const onTouchEnd = () => { tActive = false; };

      gallery.addEventListener('touchstart', onTouchStart, { passive: true });
      gallery.addEventListener('touchmove', onTouchMove, { passive: true });
      gallery.addEventListener('touchend', onTouchEnd, { passive: true });
      gallery.addEventListener('touchcancel', onTouchEnd, { passive: true });
      log('✅ bound (touch, passive)');
    }

    zd.dataset.swipeBound = '1';
  };

  const ready = () => bindSwipe();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  const mo = new MutationObserver(() => bindSwipe());
  mo.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (e) => bindSwipe(e.target));
})();
